/**
 * Responsibility: wire the modules together and drive the frame loop.
 *
 * Deliberately thin. Any logic that grows here belongs in a focused module instead — the seed-to-world
 * chain moved to `world/world.js` for exactly that reason once loading a save made it something built
 * more than once.
 */

import { createRenderer } from './render/renderer.js';
import { createTerrainView } from './render/terrain.js';
import { createChaseCamera } from './render/camera.js';
import { createAircraftMesh } from './render/aircraftMesh.js';
import { applyFog } from './render/fog.js';
import { createTileManager } from './world/tileManager.js';
import { createTileSource } from './world/tileSource.js';
import { createWorld, randomSeed } from './world/world.js';
import { createTrackView } from './render/trackView.js';
import { createTrainCamera } from './render/trainCamera.js';
import { createTrainModel, blendTrainState } from './railway/trainModel.js';
import { createFlightModel, blendFlightState } from './flight/flightModel.js';
import {
  getAircraftOrDefault,
  nextAircraft,
  DEFAULT_AIRCRAFT_ID,
} from './flight/aircraft/index.js';
import { createKeyboardInput } from './core/input.js';
import { createClock } from './core/clock.js';
import { createFrameTimer } from './core/frameTimer.js';
import { createFrameProfiler } from './core/frameProfiler.js';
import { createSaveStore } from './core/save.js';
import { createSettingsStore } from './core/settings.js';
import { createDebugOverlay } from './ui/debugOverlay.js';
import { createHud } from './ui/hud.js';
import { createMenu } from './ui/menu.js';
import { SIM, WORLD, TILES, CAMERA, DEBUG } from './config.js';

/** Milliseconds per second, for converting rAF timestamps into the seconds we simulate in. */
const MS_PER_SECOND = 1000;

function start() {
  const canvas = document.getElementById('scene');
  if (!canvas) {
    throw new Error('main: expected a canvas element with id "scene"');
  }

  const view = createRenderer(canvas);
  applyFog(view.scene);

  const clock = createClock({
    timestep: SIM.TIMESTEP,
    maxFrameTime: SIM.MAX_FRAME_TIME,
  });

  const saveStore = createSaveStore();
  const settings = createSettingsStore();

  /*
    Preferences are applied before the first frame, so a player who turned the glow down does not get
    one frame of the old value on every reload.
  */
  view.setBloomStrength(settings.get('bloomStrength'));

  /*
    The world is replaced wholesale when the seed changes. Everything downstream reaches it through
    `world`, never through a captured reference to its parts, so a reseed needs no rewiring.
  */
  /**
   * Which way the player is travelling through the world.
   *
   * **The world is rebuilt when this changes, not just the camera.** A railway shapes the ground around it —
   * cuttings, embankments, a gorge carved along the route — so the terrain in train mode is genuinely
   * different terrain, and every resident tile belongs to the other one. Switching therefore goes through the
   * same path as changing seed.
   */
  let mode = 'fly';

  let world = createWorld({ seed: WORLD.DEFAULT_SEED, railway: false });

  /*
    Stable indirections. Their identity never changes, so the chase camera and the tile manager can be
    built once and keep working across any number of seed changes.
  */
  const sampleGround = (x, z) => world.sampleGround(x, z);

  const terrain = createTerrainView(view.scene);

  /*
    Tile generation happens on a worker thread. Measured on the machine that reported the problem, a tile
    build was 21 ms of a 23 ms worst frame — more than a whole frame of work, arriving about 1.6 times a
    second, which is precisely the rate of dropped frames being seen. It cannot be tuned away on this thread.
  */
  const tileSource = createTileSource({
    buildTile: (originX, originZ) => world.buildTile(originX, originZ),
  });

  const tiles = createTileManager({
    size: WORLD.TILE_SIZE,
    loadDistance: TILES.LOAD_DISTANCE,
    evictDistance: TILES.EVICT_DISTANCE,
    maxBuildsPerUpdate: TILES.MAX_BUILDS_PER_FRAME,
    buildTile: (originX, originZ) => world.buildTile(originX, originZ),
    requestTile: tileSource.requestTile,
    mount: terrain.mount,
    unmount: terrain.unmount,
  });

  /*
    Track geometry is streamed along the *line* rather than by tile, because the track is one-dimensional —
    see `render/trackView.js`. Built once and pointed at whichever route exists, so flying costs it nothing.
  */
  const track = createTrackView(view.scene);

  const input = createKeyboardInput({ invertPitch: settings.get('invertPitch') });
  const flight = createFlightModel();
  const train = createTrainModel();

  /*
    The selected aircraft is render state only. The flight model and the camera never learn which one
    it is, which is what makes swapping instant and safe mid-flight — there is nothing to migrate.
  */
  let aircraftModel = getAircraftOrDefault(DEFAULT_AIRCRAFT_ID);
  const aircraft = createAircraftMesh(view.scene, aircraftModel);
  const chaseCamera = createChaseCamera(view.camera, {
    sampleGround,
    pitchDown: settings.get('cameraPitchDown'),
  });

  /*
    Its own controller rather than the chase camera, and the reason is a bug rather than a preference: the
    chase camera lifts itself clear of the ground, which is right for an aircraft and wrong for a cab that is
    deliberately *below* the surrounding ground every time the line runs through a cutting. See
    `render/trainCamera.js`.
  */
  const trainCamera = createTrainCamera(view.camera);
  const overlay = createDebugOverlay({ visible: settings.get('showOverlay') });
  const hud = createHud();
  const frameTimer = createFrameTimer({ sampleCount: DEBUG.FRAME_SAMPLE_COUNT });

  /*
    Separate from `frameTimer` on purpose. That one answers "how fast is it going" for the on-screen
    readout; this answers "when a frame is lost, whose fault was it", which needs per-phase timings and the
    delivered interval alongside them. See `core/frameProfiler.js`.
  */
  const profiler = createFrameProfiler({
    enabled: DEBUG.PROFILE_FRAMES,
    longFrameMs: DEBUG.LONG_FRAME_MS,
    lateFrameFactor: DEBUG.LATE_FRAME_FACTOR,
    summarySeconds: DEBUG.PROFILE_INTERVAL,
    logLimit: DEBUG.PROFILE_LOG_LIMIT,
  });

  /*
    Assigned by `enterWorld` below, which always runs before the first frame. Two states are kept so
    rendering can interpolate between them: the simulation advances in fixed steps, the display does
    not, and blending is what stops a 144 Hz monitor from showing 60 Hz stutter.
  */
  let flightState;
  let previousFlightState;

  /*
    The train's own pair, kept for the same reason and blended the same way. Both modes' states persist across
    a switch, so taking the train and coming back puts the aircraft where it was rather than resetting it.
  */
  let trainState = train.createInitialState();
  let previousTrainState = trainState;

  let simulationTime = 0;
  let nextStatsLogTime = TILES.STATS_LOG_INTERVAL;

  /** Neutral trim, in step terms, so the HUD can stay quiet while the player has not changed it. */
  const neutralTrimStep = flight.describeTrim({ speedTrim: 1 }).step;

  /**
   * Place the player in a world and build everything visible around them.
   *
   * Used by startup, by loading a save, and by starting a new seed — all three are the same operation
   * with a different origin, and having one path means a loaded world cannot be primed differently
   * from a fresh one.
   *
   * @param {object} [placement] - Passed to `flight.createInitialState`.
   */
  function enterWorld(placement = {}) {
    flightState = flight.createInitialState({ ...placement, sampleGround });
    previousFlightState = flightState;

    if (mode === 'train') {
      trainState = train.createInitialState({
        distance: placement.trainDistance,
        step: placement.trainStep ?? undefined,
      });
      previousTrainState = trainState;

      /*
        **Before anything reads the ground.** The corridor shapes terrain from the surveyed chain and the
        chain only exists as far as it has been walked, so priming a tile before the route reached it would
        produce ground that a later rebuild — or the worker — would disagree with. See `RAILWAY.PREPARE_MARGIN`.
      */
      world.railway.prepare(trainState.distance);
    }

    /*
      Every resident tile belongs to the previous world. Cleared rather than left to eviction, which
      would only drop the ones the player flew away from.
    */
    tiles.clear();

    /*
      Before priming, so anything the worker was building for the previous seed is discarded rather than
      mounted into this one. A tile from the old world is not stale terrain — it is terrain from somewhere
      else entirely. The railway flag rides along for the same reason: a tile built without the corridor is
      terrain from the other mode.
    */
    tileSource.useSeed(world.seed, { railway: mode === 'train' });

    track.useRoute(mode === 'train' ? world.railway : null);

    if (mode === 'train') {
      const pose = train.poseFor(trainState, world.railway.surveyor);

      tileSource.setPreparedTo(trainState.distance);
      tiles.prime(pose.x, pose.z);
      track.update(trainState.distance);

      // The aircraft is not in this world, and a delta parked on the rails would be absurd.
      aircraft.setVisible(false);
      trainCamera.snapTo(pose);
    } else {
      tiles.prime(flightState.x, flightState.z);

      aircraft.setVisible(true);
      aircraft.setPose(flightState);
      trainCamera.release();
      chaseCamera.snapTo(flightState, flight.velocityFor(flightState));
    }

    // A fresh world should not inherit the previous one's elapsed time or stats schedule.
    simulationTime = 0;
    nextStatsLogTime = TILES.STATS_LOG_INTERVAL;

    /*
      Both are reset because building a whole world takes far longer than a frame. Without this the
      clock would try to make up the time with a burst of steps, and the frame timer would record the
      build as the worst frame of the session for as long as its window lasts.
    */
    clock.reset();
    frameTimer.reset();
    profiler.reset();

    console.info(
      `[vibe-flyer] entered world ${world.seed}: ${tiles.getResidentCount()} tiles, ` +
        `${tiles.getSegmentCount().toLocaleString()} line segments`,
    );
  }

  /**
   * Replace the world with one from a new seed and drop the player into it.
   *
   * @param {number|string} seed
   * @param {object} [placement]
   */
  function reseed(seed, placement = {}) {
    world = createWorld({ seed, railway: mode === 'train' });
    enterWorld(placement);
  }

  /**
   * Switch between flying and the railway.
   *
   * Goes through `reseed` rather than merely repointing the camera, because **the ground is different**: a
   * railway cuts, fills and carves a gorge along its route, so every resident tile belongs to the mode that
   * built it. The same seed produces both, so switching lands you in the same world seen a different way.
   *
   * Each mode keeps its own position across the switch, which is why both states live for the whole session.
   */
  function setMode(next) {
    if (next === mode) return;
    mode = next;

    reseed(world.seed, {
      x: flightState.x,
      z: flightState.z,
      y: flightState.y,
      heading: flightState.heading,
      speedTrim: flightState.speedTrim,
      trainDistance: trainState.distance,
      trainStep: trainState.step,
    });
  }

  /** The viewer's position, whichever mode is running. What tile residency is measured from. */
  function viewerPosition() {
    if (mode !== 'train') return flightState;
    return train.poseFor(trainState, world.railway.surveyor);
  }

  /**
   * Put the player back exactly where a save left them.
   *
   * Shared by startup and the Load button, so a resumed world cannot be assembled two slightly
   * different ways. Roll and pitch are absent from the save on purpose — a loaded world starts level.
   *
   * @param {object} save - Already validated by the save store.
   */
  function applySave(save) {
    aircraftModel = getAircraftOrDefault(save.aircraftId ?? DEFAULT_AIRCRAFT_ID);
    aircraft.setModel(aircraftModel);

    // Set before reseeding, because the mode decides whether the world is built with a railway in it.
    mode = save.mode === 'train' ? 'train' : 'fly';

    reseed(save.seed, {
      x: save.worldX,
      z: save.worldZ,
      y: save.altitude,
      heading: save.heading,
      speedTrim: save.speedTrim,
      trainDistance: save.trainDistance,
      trainStep: save.trainStep,
    });
  }

  // --- pausing ------------------------------------------------------------

  let paused = false;

  /**
   * What the menu displays. Gathered fresh on every open so it can never show a stale seed or a
   * Load button enabled for a save that has since been overwritten.
   */
  function menuState() {
    return {
      seed: world.seed,
      mode,
      aircraftId: aircraftModel.id,
      hasSave: saveStore.exists(),
      settings: settings.getAll(),

      /*
        The cruise slider drives whichever vehicle is running. Both models describe their throttle the same
        way — a step, a count and a target speed — so the menu needs no notion of which one it is talking to.
      */
      cruise: mode === 'train' ? train.describeThrottle(trainState) : flight.describeTrim(flightState),
    };
  }

  /**
   * Tell the player what their speed change did.
   *
   * The step position alone does not explain the control. Naming the speed it is heading for is what
   * connects the keypress to the number already on the HUD — the first person to fly this could not tell
   * the control was doing anything, because a single step is only about a tenth of the current speed.
   */
  function announceCruise() {
    const cruise = flight.describeTrim(flightState);
    hud.showMessage(`cruise ${cruise.step} of ${cruise.steps}  ·  ${Math.round(cruise.targetSpeed)} spd`);
  }

  function openMenu() {
    if (paused) return;
    paused = true;

    /*
      Held keys are forgotten on pause. Without this, a direction held at the moment of pausing is
      still held on resume and the aircraft banks instantly — the keyup landed on the menu and never
      reached the input layer.
    */
    input.releaseAll();

    // The menu says everything; leaving the flight readout underneath competes for the same glance.
    hud.setVisible(false);

    menu.open(menuState());
  }

  function closeMenu() {
    if (!paused) return;
    paused = false;
    menu.close();
    hud.setVisible(true);

    /*
      Discard the time spent in the menu. It is real elapsed time as far as the browser is concerned,
      and without this the simulation would fast-forward through all of it on the first frame back.
    */
    clock.reset();
    frameTimer.reset();
    profiler.reset();
  }

  const menu = createMenu({
    onResume: closeMenu,

    onToggleMode() {
      setMode(mode === 'train' ? 'fly' : 'train');
      closeMenu();
      hud.showMessage(mode === 'train' ? 'On the rails' : 'Airborne');
    },

    onSave() {
      const result = saveStore.write({
        seed: world.seed,
        flightState,
        aircraftId: aircraftModel.id,

        // Both modes are always written, so switching and saving cannot lose the other one's place.
        mode,
        train: trainState,
      });

      menu.reportResult('save', result.ok ? `Saved · ${result.bytes} bytes` : 'Could not save');
    },

    onLoad() {
      const result = saveStore.read();
      if (!result.ok) {
        menu.reportResult('load', 'No usable save');
        return;
      }

      applySave(result.save);
      closeMenu();
      hud.showMessage('Resumed');
    },

    onNewWorld(seedText) {
      /*
        Empty means "surprise me". A blank field is far more likely to be a player who wants somewhere
        new than one who meant to type the seed zero.
      */
      const trimmed = seedText.trim();
      reseed(trimmed === '' ? randomSeed() : trimmed);

      closeMenu();
      hud.showMessage(`New world · ${world.seed}`);
    },

    onSelectAircraft(id) {
      aircraftModel = getAircraftOrDefault(id);
      aircraft.setModel(aircraftModel);

      // Reopened rather than left alone, so the selection marker moves to the button just clicked.
      menu.open(menuState());
    },

    onSetCruiseStep(step) {
      /*
        Applied to both states so the blend does not interpolate from the previous frame's trim. The menu
        is not reopened: doing so mid-drag would rebuild the slider under the player's cursor.
      */
      if (mode === 'train') {
        trainState = train.withStepAt(trainState, step);
        previousTrainState = train.withStepAt(previousTrainState, step);
        return;
      }

      flightState = flight.withTrimAtStep(flightState, step);
      previousFlightState = flight.withTrimAtStep(previousFlightState, step);
    },

    onSetting(name, value) {
      const stored = settings.set(name, value);

      /*
        Applied immediately, so the glow slider can be judged against the world behind the menu rather
        than by reloading and hoping.
      */
      if (name === 'bloomStrength') view.setBloomStrength(stored);
      if (name === 'invertPitch') input.setInvertPitch(stored);
      if (name === 'showOverlay') overlay.setVisible(stored);
    },
  });

  // --- startup ------------------------------------------------------------

  /*
    A save is resumed automatically. Asking first would put a dialogue between the player and flying,
    and the menu offers a new world whenever they want one.
  */
  const restored = saveStore.read();
  if (restored.ok) {
    applySave(restored.save);
  } else {
    enterWorld();
  }

  window.addEventListener('resize', () => {
    view.setSize(window.innerWidth, window.innerHeight);
  });

  /*
    Discard accumulated time when the tab is hidden. Browsers throttle or stop
    requestAnimationFrame in background tabs, so without this the first frame back would
    arrive with a huge delta and the aircraft would jump.
  */
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      clock.reset();
      // The gap while hidden is not a slow frame, and recording it would poison the peak reading.
      frameTimer.reset();
      profiler.reset();
    }
  });

  let previousTimestamp = null;

  /**
   * Periodic geometry readout. Resident and segment counts settling to a steady value is
   * what confirms tiles are being released as well as built.
   */
  function logStatsIfDue() {
    if (TILES.STATS_LOG_INTERVAL <= 0 || simulationTime < nextStatsLogTime) return;
    nextStatsLogTime = simulationTime + TILES.STATS_LOG_INTERVAL;

    console.info(
      `[vibe-flyer] t=${simulationTime.toFixed(0)}s ` +
        `mode=${mode} ` +
        `tiles=${tiles.getResidentCount()} ` +
        `segments=${tiles.getSegmentCount().toLocaleString()} ` +
        (mode === 'train'
          ? `track=${track.getSegmentCount().toLocaleString()} along=${trainState.distance.toFixed(0)}`
          : `alt=${flightState.y.toFixed(0)}`),
    );
  }

  function refreshOverlay(elapsedSeconds) {
    /*
      Checked here as well as inside the overlay, because gathering the readout costs a biome
      field sample and a ground sample. Cheap, but not worth paying for text nobody can see.
    */
    if (!overlay.isVisible()) return;

    const viewer = viewerPosition();

    overlay.update(elapsedSeconds, {
      aircraft: mode === 'train' ? { ...viewer, pitch: 0, speed: trainState.speed } : flightState,
      groundElevation: sampleGround(viewer.x, viewer.z),
      weights: world.biomeField.sample(viewer.x, viewer.z),
      tileCount: tiles.getResidentCount(),
      segmentCount: tiles.getSegmentCount(),
      frame: frameTimer.summary(),
      profile: profiler.snapshot(),
      render: view.getRenderInfo(),
      stream: {
        worker: !tileSource.isFallback(),
        pending: tileSource.getPendingCount(),
      },
      aircraftLabel: aircraftModel.label,
    });
  }

  /** Consume the edge-triggered actions. Runs whether paused or not, so Esc always reopens. */
  function handleActions() {
    const actions = input.consumeActions();
    if (actions.size === 0) return;

    if (actions.has('togglePause')) {
      if (paused) closeMenu();
      else openMenu();
    }

    // The rest only apply while flying. Trimming behind a menu would be invisible and confusing.
    if (paused) return;

    if (actions.has('cycleAircraft') && mode !== 'train') {
      aircraftModel = nextAircraft(aircraftModel.id);
      aircraft.setModel(aircraftModel);
      console.info(`[vibe-flyer] aircraft: ${aircraftModel.label}`);
    }

    if (actions.has('toggleView') && mode === 'train') {
      const now = trainCamera.toggleView();
      hud.showMessage(now === 'cab' ? 'cab view' : 'outside view');
    }

    /*
      Applied to both states so the blend does not interpolate the trim change from the previous frame's
      value. Trim is a setting, not a motion — the easing lives in actual speed, not in the target.
    */
    const trimDirection = (actions.has('trimUp') ? 1 : 0) - (actions.has('trimDown') ? 1 : 0);
    if (trimDirection !== 0) {
      if (mode === 'train') {
        trainState = train.withStep(trainState, trimDirection);
        previousTrainState = train.withStep(previousTrainState, trimDirection);

        const throttle = train.describeThrottle(trainState);
        hud.showMessage(
          throttle.stopped
            ? 'stopped'
            : `notch ${throttle.step} of ${throttle.steps}  ·  ${Math.round(throttle.targetSpeed)} spd`,
        );
      } else {
        flightState = flight.withTrimStep(flightState, trimDirection);
        previousFlightState = flight.withTrimStep(previousFlightState, trimDirection);
        announceCruise();
      }
    }

    /*
      Camera framing, not flight. Persisted on each nudge, the same as the bloom slider: this is a preference being found by
      experiment, and losing it on reload would mean starting the experiment over.
    */
    const tiltDirection =
      (actions.has('cameraTiltDown') ? 1 : 0) - (actions.has('cameraTiltUp') ? 1 : 0);

    if (tiltDirection !== 0) {
      const applied = chaseCamera.setPitchDown(
        chaseCamera.getPitchDown() + tiltDirection * CAMERA.PITCH_DOWN_STEP,
      );
      settings.set('cameraPitchDown', applied);
      hud.showMessage(`camera tilt ${applied.toFixed(0)}° down of ${CAMERA.MAX_PITCH_DOWN}°`);
    }
  }

  function frame(timestamp) {
    /*
      When this frame's work actually began, which is not the same as `timestamp`: the browser hands rAF the time it
      *scheduled* the callback, and on a late frame that can already be well in the past. The tile build deadline has to be
      measured against real elapsed work or it would let a late frame build anyway.
    */
    const frameStart = performance.now();

    const elapsedSeconds =
      previousTimestamp === null ? 0 : (timestamp - previousTimestamp) / MS_PER_SECOND;
    previousTimestamp = timestamp;

    handleActions();

    /*
      While paused the world still draws — the menu sits over a live view rather than a freeze frame,
      because a static image behind a translucent panel looks broken. Nothing advances, though: no
      steps, no camera easing, no tile updates.
    */
    if (paused) {
      view.render();
      requestAnimationFrame(frame);
      return;
    }

    frameTimer.record(elapsedSeconds);
    profiler.beginFrame(elapsedSeconds);

    const { steps, alpha } = clock.advance(elapsedSeconds);
    const controls = input.getAxes();

    for (let i = 0; i < steps; i += 1) {
      simulationTime += SIM.TIMESTEP;

      if (mode === 'train') {
        previousTrainState = trainState;

        /*
          The roll axis becomes the look axis on the rails. Reusing it rather than binding new keys is the
          honest mapping: left and right are what the player already reaches for, and on a train there is
          nothing else for them to mean — you are not steering.
        */
        trainState = train.step(trainState, { look: controls.roll }, SIM.TIMESTEP);
      } else {
        previousFlightState = flightState;
        flightState = flight.step(flightState, controls, SIM.TIMESTEP, sampleGround);
      }
    }

    profiler.mark('sim');

    /*
      **Walked out ahead of the rider before any terrain is built, and this line is load-bearing.** The
      corridor shapes ground from the surveyed chain; `surveyor.nearest` can only see chain already walked;
      the heightfield caches lattice elevations with no way to invalidate them; and the worker walks its own
      chain independently. Preparing here, before `tiles.update`, is what guarantees every tile that can be
      built has had its corridor fully determined — and telling the source how far means the worker cannot
      shape the same tile differently. See `RAILWAY.PREPARE_MARGIN`.
    */
    if (mode === 'train') {
      world.railway.prepare(trainState.distance);
      tileSource.setPreparedTo(trainState.distance);
    }

    const viewer = viewerPosition();

    /*
      **Tile residency is refreshed once per frame, not once per simulation step, and that distinction was worth several
      frames a second.**

      It used to sit inside the loop above, which quietly turned the "one tile per step" budget into "up to
      `MAX_FRAME_TIME / TIMESTEP` tiles per frame" — fifteen. Worse, `TIMESTEP` is exactly 1/60, so on a 60 Hz display the
      accumulator sits on a knife edge: any frame that runs a little long yields two steps, two steps meant two tile
      builds, and two builds guaranteed the *next* frame also ran long. A self-sustaining pair of dropped frames, which is
      exactly the 57–58 fps with intermittent hitches that was reported.

      It belongs here on principle too. What is resident around the viewer is a *view* concern: it affects nothing the
      simulation computes, so it has no business being tied to the simulation's clock. Nothing about the world depends on
      how often it is called — a tile is a pure function of its coordinates.

      The deadline then caps the damage when a frame is already late: if the simulation and the previous frame have
      already eaten the budget, this frame builds nothing and the backlog waits. It can afford to. Flight consumes about
      1.6 tiles a second against 60 opportunities, and the tiles left waiting are the furthest away — beyond the fog
      limit, where nothing can be seen arriving.
    */
    const residency = tiles.update(viewer.x, viewer.z, frameStart + TILES.BUILD_DEADLINE_MS);

    profiler.mark('tiles');

    if (steps > 0) {
      logStatsIfDue();
    }

    if (mode === 'train') {
      const blended = blendTrainState(previousTrainState, trainState, alpha);
      const pose = train.poseFor(blended, world.railway.surveyor);

      /*
        Unbudgeted, unlike terrain: a track chunk is a few hundred segments of arithmetic over stations that
        have already been walked, with no noise sampling at all.
      */
      track.update(blended.distance);
      trainCamera.update(pose, elapsedSeconds);
    } else {
      const rendered = blendFlightState(previousFlightState, flightState, alpha);
      aircraft.setPose(rendered);
      chaseCamera.update(rendered, elapsedSeconds, flight.velocityFor(rendered));
    }

    /*
      Gathered only while the readout is actually on screen. It is transient now — up for a couple of seconds
      after a speed or camera change — and altitude costs a ground sample, so building this every frame would
      be paying for numbers nobody is looking at.
    */
    hud.update(
      elapsedSeconds,
      hud.needsReadout()
        ? {
            /*
              Clearance, not absolute height. How much air is under you is the number that means something —
              and on the rails it is how deep the cutting is, which is the same measurement read the other way.
            */
            altitude: mode === 'train' ? viewer.y - sampleGround(viewer.x, viewer.z) : flightState.y - sampleGround(flightState.x, flightState.z),
            speed: mode === 'train' ? trainState.speed : flightState.speed,
            trim:
              mode === 'train'
                ? { ...train.describeThrottle(trainState), neutralStep: -1 }
                : { ...flight.describeTrim(flightState), neutralStep: neutralTrimStep },
          }
        : null,
    );
    refreshOverlay(elapsedSeconds);

    profiler.mark('view');

    view.render();

    profiler.mark('render');
    profiler.endFrame(residency.built, view.getDrawCalls());

    requestAnimationFrame(frame);
  }

  requestAnimationFrame(frame);
}

start();
